/**
 * Cross-source reconciliation — V102-5-2 / ADR-0060 §1.
 *
 * A run joins two populations of one workspace (population-service) with a
 * reviewed recipe from `RECONCILIATION_RECIPES` and records the mismatches.
 * Recipes are code, not customer SQL: each names the population kinds on
 * both sides, the join key, and the rules that turn a joined (or unjoined)
 * key into a mismatch.
 *
 * Identity safety (V101-4-5, "raw emails never reach OLTP"):
 *   - an identity recipe joins on keyed hashes only. Both populations must be
 *     collector snapshots with the same `identity_hash_version`; a v1 and a
 *     v2 snapshot (or an upload, which has none) are never compared — the run
 *     is refused;
 *   - every join value and item key on an identity recipe must look like a
 *     hash (64 hex); anything else refuses the run;
 *   - mismatch detail carries only the attributes the rule names, and any
 *     string that looks like an email address is dropped.
 *
 * Integrity: both populations must still hash to their recorded
 * `content_sha256`. The run stores both hashes, counts and `result_sha256`
 * (SHA-256 over the canonical result), so an assessor can recompute it.
 * The run row is written with `completed_at` NULL, then its mismatches, then
 * `completed_at` is stamped once (trigger-enforced). Only completed runs are
 * evidence.
 *
 * A mismatch never opens a finding (owner choice, ADR-0060 §1): the evaluator
 * proposes from a run, and a finding opens only when a person reviews a Not
 * met determination that cites it (determination-service).
 *
 * Every function takes `workspaceId` and scopes every query by it.
 */
import { canonicalJson, generateRandomId, sha256Hex } from '../utils/audit-crypto.js';
import { getPopulation, getPopulationItems, populationContentSha256 } from './population-service.js';
import { getTest } from './assessment-test-service.js';
export const RECONCILIATION_RESULT_FORMAT = 'gacpro.reconciliation/1';
export const MISMATCH_KINDS = ['left_only', 'right_only', 'attribute', 'timing'];
const DAY_MS = 86_400_000;
const HASH_RE = /^[0-9a-f]{64}$/;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const ROWS_PER_INSERT = 10; // 10 columns per mismatch row → 100 bound parameters
const STATEMENTS_PER_BATCH = 40;
export class ReconciliationError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'ReconciliationError';
    }
}
const fail = (code, m) => {
    throw new ReconciliationError(code, m);
};
const lower = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');
const dateMs = (v) => {
    if (typeof v !== 'string' || !v.trim())
        return null;
    const t = Date.parse(v.length === 10 ? `${v}T00:00:00.000Z` : v);
    return Number.isNaN(t) ? null : t;
};
const DISABLED_ACCOUNT = new Set(['disabled', 'deprovisioned', 'deactivated', 'suspended', 'inactive', 'deleted', 'blocked']);
/** An IdP account is enabled unless it says otherwise. */
export function accountEnabled(i) {
    if (i.attributes.enabled === false)
        return false;
    return !DISABLED_ACCOUNT.has(lower(i.attributes.status));
}
const TERMINATED_WORKER = new Set(['left', 'terminated', 'inactive', 'separated', 'relieved']);
const SUSPENDED_WORKER = new Set(['suspended', 'on_leave', 'leave']);
/** A roster worker's state as of a time (HRIS status + termination / relieving date). */
export function workerState(i, asOfMs) {
    const status = lower(i.attributes.employment_status ?? i.attributes.status);
    const endMs = dateMs(i.attributes.terminated_at ?? i.attributes.relieving_date ?? i.attributes.end_date);
    if (TERMINATED_WORKER.has(status) || (endMs !== null && endMs <= asOfMs))
        return { state: 'terminated', terminatedAtMs: endMs };
    if (SUSPENDED_WORKER.has(status))
        return { state: 'suspended', terminatedAtMs: null };
    return { state: 'active', terminatedAtMs: null };
}
const idpVsHr = {
    key: 'idp_vs_hr',
    version: 1,
    title: 'IdP accounts ↔ HR roster',
    left: {
        label: 'IdP accounts',
        kinds: ['idp_accounts', 'workforce_users', 'okta_active_user', 'entra_enabled_user'],
        joinAttribute: 'email_hash',
        onMissingJoinKey: { kind: 'attribute', rule_key: 'account_unmatchable', relevant: true },
    },
    right: {
        label: 'HR roster',
        kinds: ['hr_workers', 'hris_workers', 'workers'],
        joinAttribute: 'email_hash',
        onMissingJoinKey: { kind: 'attribute', rule_key: 'worker_unmatchable', relevant: false },
    },
    identityJoin: true,
    params: { grace_days: { default: 1, min: 0, max: 90 } },
    rules: {
        enabled_without_worker: { kind: 'left_only', relevant: true, description: 'An enabled account has no current worker.' },
        terminated_still_enabled: { kind: 'timing', relevant: true, description: 'A worker terminated more than the grace period ago still has an enabled account.' },
        terminated_within_grace: { kind: 'timing', relevant: false, description: 'A terminated worker’s account is still enabled inside the grace period.' },
        suspended_worker_enabled: { kind: 'attribute', relevant: false, description: 'A suspended worker’s account is enabled.' },
        worker_without_account: { kind: 'right_only', relevant: false, description: 'An active worker has no account.' },
        account_unmatchable: { kind: 'attribute', relevant: true, description: 'An account carries no join key.' },
        worker_unmatchable: { kind: 'attribute', relevant: false, description: 'A worker carries no join key.' },
    },
    compare({ left, right, asOfMs, params }) {
        const enabled = left.filter(accountEnabled);
        const states = right.map((w) => ({ w, ...workerState(w, asOfMs) }));
        if (!left.length) {
            const active = states.filter((s) => s.state === 'active').map((s) => s.w);
            return active.length ? [{ kind: 'right_only', rule_key: 'worker_without_account', relevant: false, right: active }] : [];
        }
        if (!enabled.length)
            return [];
        if (!right.length)
            return [{ kind: 'left_only', rule_key: 'enabled_without_worker', relevant: true, left: enabled }];
        if (states.some((s) => s.state === 'active'))
            return [];
        if (states.some((s) => s.state === 'suspended')) {
            return [{ kind: 'attribute', rule_key: 'suspended_worker_enabled', relevant: false, left: enabled, right, detail: { worker_state: 'suspended' } }];
        }
        // Every record for the person is terminated: the latest termination decides.
        const dates = states.map((s) => s.terminatedAtMs).filter((t) => t !== null);
        const terminatedAtMs = dates.length === states.length ? Math.max(...dates) : null;
        const days = terminatedAtMs === null ? null : Math.floor((asOfMs - terminatedAtMs) / DAY_MS);
        const grace = params.grace_days;
        const over = days === null || days > grace;
        return [{
                kind: 'timing', rule_key: over ? 'terminated_still_enabled' : 'terminated_within_grace', relevant: over, left: enabled, right,
                detail: { days_since_termination: days, grace_days: grace, terminated_on: terminatedAtMs === null ? null : new Date(terminatedAtMs).toISOString().slice(0, 10) },
            }];
    },
};
const APPROVED_TICKET = new Set(['approved', 'implemented', 'closed', 'done', 'completed', 'resolved']);
const deploysVsTickets = {
    key: 'deploys_vs_tickets',
    version: 1,
    title: 'Production deploys ↔ change tickets',
    left: {
        label: 'Deploys',
        kinds: ['deploys', 'deployments', 'production_deploys'],
        joinAttribute: 'ticket_ref',
        onMissingJoinKey: { kind: 'left_only', rule_key: 'deploy_without_ticket', relevant: true },
    },
    right: {
        label: 'Change tickets',
        kinds: ['change_tickets', 'tickets', 'changes'],
        joinAttribute: null,
        onMissingJoinKey: { kind: 'right_only', rule_key: 'ticket_unmatchable', relevant: false },
    },
    identityJoin: false,
    params: {},
    rules: {
        deploy_without_ticket: { kind: 'left_only', relevant: true, description: 'A deploy names no change ticket, or one that does not exist.' },
        ticket_not_approved: { kind: 'attribute', relevant: true, description: 'A deploy’s ticket was never approved.' },
        approved_after_deploy: { kind: 'timing', relevant: true, description: 'A deploy shipped before its ticket was approved.' },
        ticket_unmatchable: { kind: 'right_only', relevant: false, description: 'A ticket carries no key.' },
    },
    compare({ left, right }) {
        if (!left.length)
            return [];
        if (!right.length)
            return [{ kind: 'left_only', rule_key: 'deploy_without_ticket', relevant: true, left }];
        const ticket = right[0];
        const out = [];
        const status = lower(ticket.attributes.status);
        if (status && !APPROVED_TICKET.has(status)) {
            out.push({ kind: 'attribute', rule_key: 'ticket_not_approved', relevant: true, left, right: [ticket], detail: { ticket_status: status } });
            return out;
        }
        const approvedMs = dateMs(ticket.attributes.approved_at);
        const early = approvedMs === null ? [] : left.filter((d) => { const t = dateMs(d.occurred_at ?? d.attributes.deployed_at); return t !== null && t < approvedMs; });
        if (early.length)
            out.push({ kind: 'timing', rule_key: 'approved_after_deploy', relevant: true, left: early, right: [ticket] });
        return out;
    },
};
const RETIRED_DEVICE = new Set(['retired', 'disposed', 'lost', 'stolen', 'decommissioned']);
const devicesVsInventory = {
    key: 'devices_vs_inventory',
    version: 1,
    title: 'Managed devices ↔ asset inventory',
    left: {
        label: 'Managed devices',
        kinds: ['managed_devices', 'mdm_devices', 'edr_devices', 'devices'],
        joinAttribute: 'serial',
        onMissingJoinKey: { kind: 'attribute', rule_key: 'device_without_serial', relevant: true },
    },
    right: {
        label: 'Asset inventory',
        kinds: ['asset_inventory', 'device_inventory', 'inventory_devices'],
        joinAttribute: 'serial',
        onMissingJoinKey: { kind: 'attribute', rule_key: 'inventory_without_serial', relevant: true },
    },
    identityJoin: false,
    params: {},
    rules: {
        managed_device_not_in_inventory: { kind: 'left_only', relevant: true, description: 'A managed device is missing from the inventory.' },
        inventory_device_unmanaged: { kind: 'right_only', relevant: true, description: 'An in-service inventory device is not managed.' },
        retired_device_still_managed: { kind: 'attribute', relevant: true, description: 'A retired inventory device still reports to device management.' },
        device_without_serial: { kind: 'attribute', relevant: true, description: 'A managed device carries no serial.' },
        inventory_without_serial: { kind: 'attribute', relevant: true, description: 'An inventory device carries no serial.' },
    },
    compare({ left, right }) {
        const inService = right.filter((r) => !RETIRED_DEVICE.has(lower(r.attributes.status)));
        if (!right.length)
            return left.length ? [{ kind: 'left_only', rule_key: 'managed_device_not_in_inventory', relevant: true, left }] : [];
        if (!left.length)
            return inService.length ? [{ kind: 'right_only', rule_key: 'inventory_device_unmanaged', relevant: true, right: inService }] : [];
        if (!inService.length)
            return [{ kind: 'attribute', rule_key: 'retired_device_still_managed', relevant: true, left, right, detail: { inventory_status: lower(right[0].attributes.status) } }];
        return [];
    },
};
const privilegedVsApprovals = {
    key: 'privileged_vs_approvals',
    version: 1,
    title: 'Privileged roles ↔ access approvals',
    left: {
        label: 'Privileged principals',
        kinds: ['privileged_principals', 'admins', 'okta_admin_user', 'entra_global_admin_principal'],
        joinAttribute: null,
        onMissingJoinKey: { kind: 'attribute', rule_key: 'principal_unmatchable', relevant: true },
    },
    right: {
        label: 'Access approvals',
        kinds: ['privileged_access_approvals', 'access_approvals'],
        joinAttribute: 'principal_hash',
        onMissingJoinKey: { kind: 'attribute', rule_key: 'approval_unmatchable', relevant: false },
    },
    identityJoin: true,
    params: {},
    rules: {
        privileged_without_approval: { kind: 'left_only', relevant: true, description: 'A privileged principal has no approval.' },
        approval_expired: { kind: 'timing', relevant: true, description: 'A privileged principal’s approval has expired.' },
        approval_revoked: { kind: 'attribute', relevant: true, description: 'A privileged principal’s approval was revoked.' },
        approval_without_privilege: { kind: 'right_only', relevant: false, description: 'An approval names no current privileged principal.' },
        principal_unmatchable: { kind: 'attribute', relevant: true, description: 'A principal carries no key.' },
        approval_unmatchable: { kind: 'attribute', relevant: false, description: 'An approval carries no principal key.' },
    },
    compare({ left, right, asOfMs }) {
        if (!left.length)
            return right.length ? [{ kind: 'right_only', rule_key: 'approval_without_privilege', relevant: false, right }] : [];
        if (!right.length)
            return [{ kind: 'left_only', rule_key: 'privileged_without_approval', relevant: true, left }];
        const live = right.filter((a) => {
            if (lower(a.attributes.status) === 'revoked')
                return false;
            const exp = dateMs(a.attributes.expires_at);
            return exp === null || exp > asOfMs;
        });
        if (live.length)
            return [];
        const revoked = right.some((a) => lower(a.attributes.status) === 'revoked');
        return [{ kind: revoked ? 'attribute' : 'timing', rule_key: revoked ? 'approval_revoked' : 'approval_expired', relevant: true, left, right }];
    },
};
/** The reviewed recipes (ADR-0060 §1). A new recipe or a change to one is a code review. */
export const RECONCILIATION_RECIPES = Object.freeze({
    [idpVsHr.key]: idpVsHr,
    [deploysVsTickets.key]: deploysVsTickets,
    [devicesVsInventory.key]: devicesVsInventory,
    [privilegedVsApprovals.key]: privilegedVsApprovals,
});
export function getReconciliationRecipe(key) {
    return Object.prototype.hasOwnProperty.call(RECONCILIATION_RECIPES, key) ? RECONCILIATION_RECIPES[key] : null;
}
function joinValue(side, i) {
    const v = side.joinAttribute === null ? i.item_key : i.attributes[side.joinAttribute];
    if (v === null || v === undefined)
        return null;
    const s = String(v).trim();
    return s ? (side.joinAttribute === null ? s : s.toLowerCase()) : null;
}
/** Drop anything that looks like an email (defence in depth: identities never land in detail). */
function sanitizeDetail(d) {
    const out = {};
    for (const [k, v] of Object.entries(d ?? {})) {
        if (typeof v === 'string' && EMAIL_RE.test(v))
            continue;
        out[k] = typeof v === 'string' ? v.slice(0, 200) : v;
    }
    return out;
}
/**
 * Join two item lists with a recipe. Pure apart from hashing. Throws
 * `invalid` when an identity recipe meets a value that is not a keyed hash.
 */
export async function computeReconciliation(recipe, left, right, opts) {
    const keyHash = async (k) => (recipe.identityJoin ? k : sha256Hex(k));
    const index = (side, items, label) => {
        const map = new Map();
        const missing = [];
        for (const i of items) {
            if (recipe.identityJoin && !HASH_RE.test(i.item_key)) {
                fail('invalid', `${label} item keys must be keyed identity hashes; refusing to reconcile raw identifiers.`);
            }
            const k = joinValue(side, i);
            if (k === null) {
                missing.push(i);
                continue;
            }
            if (recipe.identityJoin && !HASH_RE.test(k)) {
                fail('invalid', `${label} join values (${side.joinAttribute ?? 'item key'}) must be keyed identity hashes; refusing to reconcile raw identifiers.`);
            }
            const l = map.get(k) ?? [];
            l.push(i);
            map.set(k, l);
        }
        return { map, missing };
    };
    const L = index(recipe.left, left, recipe.left.label);
    const R = index(recipe.right, right, recipe.right.label);
    const itemKeys = (items) => (items ?? []).map((i) => i.item_key).sort();
    const mismatches = [];
    for (const [side, miss] of [['left', L.missing], ['right', R.missing]]) {
        const rule = recipe[side].onMissingJoinKey;
        for (const i of miss) {
            const h = await keyHash(i.item_key);
            mismatches.push({
                kind: rule.kind, rule_key: rule.rule_key, relevant: rule.relevant,
                left_key_hash: side === 'left' ? h : null, right_key_hash: side === 'right' ? h : null,
                detail: { [`${side}_item_keys`]: [i.item_key], reason: 'join_key_missing' },
            });
        }
    }
    const keys = [...new Set([...L.map.keys(), ...R.map.keys()])].sort();
    let matched = 0;
    for (const k of keys) {
        const l = L.map.get(k) ?? [];
        const r = R.map.get(k) ?? [];
        if (l.length && r.length)
            matched++;
        const h = await keyHash(k);
        for (const m of recipe.compare({ left: l, right: r, asOfMs: opts.asOfMs, params: opts.params })) {
            const rule = recipe.rules[m.rule_key];
            if (!rule || rule.kind !== m.kind || rule.relevant !== m.relevant)
                throw new Error(`Recipe ${recipe.key} emitted an undeclared rule ${m.rule_key}.`);
            const detail = sanitizeDetail(m.detail);
            if (m.left?.length)
                detail.left_item_keys = itemKeys(m.left);
            if (m.right?.length)
                detail.right_item_keys = itemKeys(m.right);
            mismatches.push({
                kind: m.kind, rule_key: m.rule_key, relevant: m.relevant,
                left_key_hash: l.length ? h : null, right_key_hash: r.length ? h : null, detail,
            });
        }
    }
    mismatches.sort((a, b) => {
        const x = canonicalJson(a);
        const y = canonicalJson(b);
        return x < y ? -1 : x > y ? 1 : 0;
    });
    return { left_count: left.length, right_count: right.length, matched_count: matched, mismatches };
}
function resolveParams(recipe, raw) {
    const out = {};
    for (const k of Object.keys(raw ?? {}))
        if (!recipe.params[k])
            fail('invalid', `Recipe ${recipe.key} has no parameter ${k}.`);
    for (const [k, spec] of Object.entries(recipe.params)) {
        const v = raw?.[k] ?? spec.default;
        if (!Number.isInteger(v) || v < spec.min || v > spec.max)
            fail('invalid', `${k} must be a whole number from ${spec.min} to ${spec.max}.`);
        out[k] = v;
    }
    return out;
}
async function loadSide(db, workspaceId, periodId, populationId, side) {
    const pop = await getPopulation(db, workspaceId, populationId);
    if (!pop)
        fail('not_found', `${side.label}: population not found.`);
    if (!side.kinds.includes(pop.kind))
        fail('invalid', `${side.label} must be one of: ${side.kinds.join(', ')} (got ${pop.kind}).`);
    if (pop.period_id && pop.period_id !== periodId)
        fail('invalid', `${side.label}: the population belongs to another audit period.`);
    const items = (await getPopulationItems(db, workspaceId, populationId));
    if (items.length !== pop.item_count || (await populationContentSha256(items)) !== pop.content_sha256) {
        fail('integrity', `${side.label}: population items no longer match their recorded hash; refusing to reconcile.`);
    }
    const version = pop.identity_hash_version;
    return { pop: pop, items, identityHashVersion: version === null || version === undefined ? null : Number(version) };
}
/** Run a recipe over two populations and store the run + mismatches. */
export async function runReconciliation(db, input) {
    const recipe = getReconciliationRecipe(input.recipeKey);
    if (!recipe)
        fail('invalid', `Unknown reconciliation recipe ${input.recipeKey}.`);
    const runBy = typeof input.runBy === 'string' ? input.runBy.trim() : '';
    if (!runBy)
        fail('invalid', 'runBy is required.');
    if (input.leftPopulationId === input.rightPopulationId)
        fail('invalid', 'A run joins two different populations.');
    const period = (await db.query(`SELECT period_id, period_end, status FROM audit_periods WHERE period_id = ? AND workspace_id = ? LIMIT 1`, [input.periodId, input.workspaceId])).results[0];
    if (!period)
        fail('not_found', 'Audit period not found.');
    if (period.status === 'closed')
        fail('conflict', 'This period is closed.');
    if (input.testKey) {
        const t = await getTest(input.contentDb ?? db, input.testKey);
        if (!t)
            fail('invalid', `Unknown test ${input.testKey}.`);
    }
    if (input.assetId) {
        const a = await db.query(`SELECT 1 AS ok FROM assets WHERE asset_id = ? AND workspace_id = ? LIMIT 1`, [input.assetId, input.workspaceId]);
        if (!a.results.length)
            fail('not_found', 'Asset not found.');
    }
    const params = resolveParams(recipe, input.params);
    const left = await loadSide(db, input.workspaceId, input.periodId, input.leftPopulationId, recipe.left);
    const right = await loadSide(db, input.workspaceId, input.periodId, input.rightPopulationId, recipe.right);
    let identityHashVersion = null;
    if (recipe.identityJoin) {
        if (left.identityHashVersion === null || right.identityHashVersion === null) {
            fail('hash_version_mismatch', 'Identity reconciliation needs two collector snapshots with a recorded identity hash version; uploads are not pseudonymized.');
        }
        if (left.identityHashVersion !== right.identityHashVersion) {
            fail('hash_version_mismatch', `The populations use different identity hash versions (v${left.identityHashVersion} and v${right.identityHashVersion}); re-collect so both use the same scheme.`);
        }
        identityHashVersion = left.identityHashVersion;
    }
    const now = input.now ?? new Date();
    const periodEndExclMs = Date.parse(`${period.period_end}T00:00:00.000Z`) + DAY_MS;
    const asOfMs = Math.min((input.asOf ?? now).getTime(), periodEndExclMs);
    const asOf = new Date(asOfMs).toISOString();
    const computed = await computeReconciliation(recipe, left.items, right.items, { asOfMs, params });
    const relevant = computed.mismatches.filter((m) => m.relevant).length;
    const assetId = input.assetId ?? null;
    const resultSha = await sha256Hex(canonicalJson({
        format: RECONCILIATION_RESULT_FORMAT,
        recipe: { key: recipe.key, version: recipe.version },
        params,
        as_of: asOf,
        identity_hash_version: identityHashVersion,
        left: { population_id: left.pop.population_id, content_sha256: left.pop.content_sha256 },
        right: { population_id: right.pop.population_id, content_sha256: right.pop.content_sha256 },
        counts: { left: computed.left_count, right: computed.right_count, matched: computed.matched_count, mismatches: computed.mismatches.length, relevant },
        asset_id: assetId,
        mismatches: computed.mismatches,
    }));
    const runId = generateRandomId('recon');
    const at = now.toISOString();
    await db.execute(`INSERT INTO reconciliation_runs (
       run_id, workspace_id, period_id, recipe_key, recipe_version, test_key,
       left_population_id, right_population_id, left_population_sha256, right_population_sha256,
       identity_hash_version, params_json, as_of, left_count, right_count, matched_count,
       mismatch_count, relevant_mismatch_count, result_sha256, run_by, created_at
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [runId, input.workspaceId, input.periodId, recipe.key, recipe.version, input.testKey ?? null,
        left.pop.population_id, right.pop.population_id, left.pop.content_sha256, right.pop.content_sha256,
        identityHashVersion, canonicalJson(params), asOf, computed.left_count, computed.right_count, computed.matched_count,
        computed.mismatches.length, relevant, resultSha, runBy, at]);
    const statements = [];
    for (let i = 0; i < computed.mismatches.length; i += ROWS_PER_INSERT) {
        const chunk = computed.mismatches.slice(i, i + ROWS_PER_INSERT);
        // d1-in-list: bounded — ROWS_PER_INSERT (10) rows × 10 columns = 100 parameters
        const values = chunk.map(() => '(?, ?, ?, ?, ?, ?, ?, ?, ?, ?)').join(', ');
        statements.push({
            sql: `INSERT INTO reconciliation_mismatches (mismatch_id, run_id, workspace_id, kind, rule_key, relevant, left_key_hash, right_key_hash, detail_json, asset_id) VALUES ${values}`,
            params: chunk.flatMap((m) => [generateRandomId('rmm'), runId, input.workspaceId, m.kind, m.rule_key, m.relevant ? 1 : 0,
                m.left_key_hash, m.right_key_hash, canonicalJson(m.detail), assetId]),
        });
    }
    for (let i = 0; i < statements.length; i += STATEMENTS_PER_BATCH)
        await db.batch(statements.slice(i, i + STATEMENTS_PER_BATCH));
    await db.execute(`UPDATE reconciliation_runs SET completed_at = ? WHERE run_id = ? AND workspace_id = ? AND completed_at IS NULL`, [at, runId, input.workspaceId]);
    return { run: (await getReconciliationRun(db, input.workspaceId, runId)), mismatches: computed.mismatches.length };
}
export async function getReconciliationRun(db, workspaceId, runId) {
    const r = await db.query(`SELECT * FROM reconciliation_runs WHERE run_id = ? AND workspace_id = ? LIMIT 1`, [runId, workspaceId]);
    return r.results[0] ?? null;
}
export async function listReconciliationRuns(db, workspaceId, f = {}) {
    const where = ['workspace_id = ?'];
    const params = [workspaceId];
    if (f.periodId) {
        where.push('period_id = ?');
        params.push(f.periodId);
    }
    if (f.testKey) {
        where.push('test_key = ?');
        params.push(f.testKey);
    }
    if (f.recipeKey) {
        where.push('recipe_key = ?');
        params.push(f.recipeKey);
    }
    if (f.completedOnly)
        where.push('completed_at IS NOT NULL');
    const r = await db.query(`SELECT * FROM reconciliation_runs WHERE ${where.join(' AND ')} ORDER BY as_of DESC, run_id`, params);
    return r.results;
}
export async function listReconciliationMismatches(db, workspaceId, runId, f = {}) {
    const r = await db.query(`SELECT * FROM reconciliation_mismatches WHERE workspace_id = ? AND run_id = ?${f.relevantOnly ? ' AND relevant = 1' : ''} ORDER BY kind, rule_key, mismatch_id`, [workspaceId, runId]);
    return r.results;
}
/** Relevant mismatches of a run that bear on one asset (unstamped ones bear on every asset). */
export async function relevantMismatchCount(db, workspaceId, run, assetId) {
    if (assetId === null)
        return Number(run.relevant_mismatch_count);
    const r = await db.query(`SELECT COUNT(*) AS n FROM reconciliation_mismatches
      WHERE workspace_id = ? AND run_id = ? AND relevant = 1 AND (asset_id IS NULL OR asset_id = ?)`, [workspaceId, run.run_id, assetId]);
    return Number(r.results[0]?.n ?? 0);
}
//# sourceMappingURL=reconciliation-service.js.map